import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

/**
 * Formats a number to a specified number of decimal places.
 * If a threshold is provided, the number will be formatted with no decimal places if it is greater than or equal to the threshold.
 *
 * @param value - The number to format.
 * @param fractionDigits - The number of decimal places to include in the formatted number.
 * @param threshold - An optional threshold above which the number will be formatted with no decimal places.
 * @returns The formatted number as a string.
 */
export const formatNumber = (value: number, fractionDigits = 0, threshold?: number): string => {
  if (threshold === undefined) {
    return value.toFixed(fractionDigits);
  }

  if (value >= threshold) {
    return value.toFixed(0);
  }

  return value.toFixed(fractionDigits);
};

export const formatTimestamp = (timestamp: number, options?: { showWeekday?: boolean }) => {
  const date = new Date(timestamp);

  const year = date.getFullYear();
  const month = date.getMonth() + 1; // Months are 0-based
  const day = date.getDate();

  // Get weekday in Japanese
  const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
  const weekday = weekdays[date.getDay()];

  // Get hours and minutes in 24-hour format
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  if (options?.showWeekday) {
    return `${year}-${month}月${day}日（${weekday}）${hours}:${minutes}`;
  }
  return `${year}-${month}月${day}日 ${hours}:${minutes}`;
};

/**
 * Formats a timestamp as relative time from now.
 *
 * @param timestamp - The timestamp to format
 * @returns Formatted time string (e.g., "5y", "3m", "2d", "5h", "30min")
 */
export const formatRelativeTimestamp = (timestamp: number) => {
  const now = new Date();
  const date = new Date(timestamp);

  const years = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24 * 365));
  if (years >= 1) {
    return `${years}y`;
  }

  const months = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24 * 30));
  if (months >= 1) {
    return `${months}m`;
  }

  const days = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days >= 1) {
    return `${days}d`;
  }

  const hours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  if (hours >= 1) {
    return `${hours}h`;
  }

  const minutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  if (minutes >= 1) {
    return `${minutes}min`;
  }

  return "now";
};

export const getColorStyleByRank = (rank: number) => {
  switch (rank) {
    case 1:
      return "bg-gradient-to-b from-[#FFE500] to-[#3D4700] bg-clip-text text-transparent font-bold";
    case 2:
      return "bg-gradient-to-b from-[#FFFFFF] to-[#414141] bg-clip-text text-transparent font-bold";
    case 3:
      return "bg-gradient-to-b from-[#CBA080] to-[#693F0D] bg-clip-text text-transparent font-bold";
    default:
      if (rank <= 10) {
        return "text-disabled text-xl font-medium";
      }

      if (rank <= 100) {
        return "text-disabled text-lg font-medium";
      }

      return "text-disabled text-lg font-medium";
  }
};

export const getBackgroundColorByRank = (rank: string) => {
  switch (rank) {
    case "1":
      return "bg-[#FFF5CC]";
    case "2":
      return "bg-[#ECF0F0]";
    case "3":
      return "bg-[#FAEEE6]";
    default:
      return "bg-[#fff9f5]";
  }
};

// Export validation utilities
export {
  validateCommentInput,
  validateUsername,
  COMMENT_VALIDATION_CONSTANTS,
  USERNAME_VALIDATION_CONSTANTS,
} from "./validation";
